---
title: Začínáme
sidebar_position: 2
description: Začínáme
---

# Začínáme

## Přihlášení

### První přihlášení pomocí účtu Planet

Pokud máte účet Planet, postupujte podle následujících kroků pro přístup do aplikace AM:

#### Krok 1: Přejděte do aplikace

1. Otevřete webový prohlížeč
2. Přejděte na [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. Zobrazí se přihlašovací obrazovka

![Přihlašovací obrazovka aplikace AM se zvýrazněným tlačítkem účtu Planet](../imgs/login-planet.png)

#### Krok 2: Zvolte přihlášení pomocí účtu Planet

1. Na přihlašovací obrazovce klikněte na tlačítko **„Planet Account"**
2. Budete přesměrováni na ověřovací stránku Planet

#### Krok 3: Propojení účtů (pouze při prvním přihlášení)

Pokud se přihlašujete pomocí účtu Planet poprvé:

1. Po ověření pomocí Planet se zobrazí obrazovka pro propojení účtů
2. Vyberte možnost **„Add to existing account"**
3. Na vaši e-mailovou adresu bude odeslán ověřovací e-mail z adresy `noreply@planet.com`
4. **Důležité**: Zkontrolujte e-mail a klikněte na potvrzovací odkaz do **5 minut**
5. Pokud odkaz vyprší, bude nutné přihlašovací proces opakovat od začátku

#### Krok 4: Přijetí podmínek použití

1. Přečtěte si podmínky použití Planet Labs
2. Zaškrtněte políčko pro potvrzení
3. Klikněte na `Accept` a pokračujte

#### Krok 5: Přístup udělen

Po ověření budete přesměrováni na Přehled a můžete začít aplikaci používat.

---

### První přihlášení pomocí dedikovaných přihlašovacích údajů AMS

Pokud jste obdrželi dedikované přihlašovací údaje pro službu Area Monitoring Service:

#### Krok 1: Přejděte do aplikace

1. Otevřete webový prohlížeč
2. Přejděte na [https://area-monitoring.planet.com](https://area-monitoring.planet.com)
3. Zobrazí se přihlašovací obrazovka

![Přihlašovací obrazovka se zvýrazněnými poli pro uživatelské jméno a heslo](../imgs/login-ams.png)

#### Krok 2: Zadejte přihlašovací údaje

1. Zadejte své **uživatelské jméno** nebo **e-mail**
2. Zadejte své **heslo**
3. Klikněte na `Sign In`

#### Krok 3: Přijetí podmínek použití

1. Při prvním přihlášení si přečtěte podmínky použití Planet Labs
2. Zaškrtněte políčko pro potvrzení
3. Klikněte na `Accept` a pokračujte

#### Krok 4: Přístup udělen

Po ověření budete přesměrováni na Přehled a můžete začít aplikaci používat.

## Výběr rozsahu

Po přihlášení je vaším prvním úkolem výběr rozsahu (scope):

1. V levé části záhlaví aplikace najděte **rozbalovací nabídku rozsahu**
2. Kliknutím na ni zobrazíte dostupné rozsahy _(pokud je k dispozici pouze jeden rozsah, rozbalovací nabídka se nezobrazí)_

![Rozbalovací nabídka výběru rozsahu se zvýrazněnou položkou `DEMO_FR23`](../imgs/scope-selection.png)

### Výběr rozsahu

- `DEMO_FR23`: Demonstrační rozsah dostupný všem uživatelům s účtem Planet
- **Projektově specifické rozsahy**, ke kterým musí přístup udělit správce uživatelů daného rozsahu

1. Klikněte na název rozsahu, abyste ho vybrali
2. Aplikace načte zákresy JŽ a data pro daný rozsah
3. Veškerá následující data a filtry budou specifická pro tento rozsah

**Důležité**: Vybraný rozsah určuje:

- Které geografické oblasti monitorujete
- Která časová období mají dostupná data
- Které signály a indikátory jsou vypočítávány
- Všechny možnosti filtrování v celé aplikaci

Rozsah lze kdykoli změnit opětovným použitím rozbalovací nabídky rozsahu.

## Přehled rozložení aplikace

**Modul Přehled** je vaším primárním vstupním bodem po výběru rozsahu. Poskytuje:

- Celkový pohled na všechny zákresy JŽ (Features of Interest, FOI) ve vašem rozsahu
- Prostorovou mapu s umístěním zákresů JŽ
- Rychlý přístup k podrobné analýze zákresu JŽ (Pohled zákresu JŽ)
- Různé metody vyhledávání a filtrování zákresů JŽ
- Přizpůsobitelné rozložení — přetažením oddělovače mezi tabulkou a mapou změníte jejich poměr

Modul Přehled slouží k:

- Procházení a průzkumu zákresů JŽ v monitorované oblasti
- Identifikaci zákresů JŽ, které vás zajímají
- Navigaci do podrobné analýzy v modulu Pohled zákresu JŽ

![Přehled](../imgs/overview.png)

### Hlavní komponenty

**Záhlaví** (nahoře):

- **Rozbalovací nabídka rozsahu**: Přepínání mezi dostupnými rozsahy; při práci v Validační konzoli se zde zobrazuje také aktivní ID sady a ID balíčku
- **Přepínač motivu**: tmavý nebo světlý
- **Menu aplikací**: Přechod do jiných aplikací Planet
- **Uživatelské menu**: Přístup k profilu, nastavení jazyka a odhlášení

**Sbalitelné menu modulů** (vlevo) — hlavní pracovní prostor se mění podle zvoleného modulu:

- **Datové zobrazení**:
  - **Přehled** (výchozí): Seznam a mapa zákresů JŽ
  - **Pohled zákresu JŽ**: Podrobná analýza zákresu JŽ s více panely
- **Validační konzole** (viditelná uživatelům s přístupem do Validační konzole):
  - **Vytvoření sady**: Definice typů vstupů a filtrů pro vytvoření sady balíčků _(pouze role: GTC-ADMIN)_
  - **Sady**: Seznam vytvořených sad _(role: GTC-ADMIN, GTC-OPERATOR nebo GTC-VIEWER)_
  - **Balíčky**: Seznam vytvořených balíčků _(role: GTC-ADMIN, GTC-OPERATOR nebo GTC-VIEWER)_
  - **Pohled validace**: Analýza zákresu JŽ na úrovni balíčku s více panely a vstupem uživatele _(role: GTC-ADMIN, GTC-OPERATOR nebo GTC-VIEWER)_
- **Konfigurace**:
  - **Nastavení**: Nastavení časového období a kontextu indikátorů
  - **Editor konfigurace** (Config Editor): Úprava konfigurace rozsahu _(pouze role: SUPER-SYSADMIN)_

Centrální oblast zobrazuje komponenty specifické pro vybraný modul.

![Hlavní komponenty](../imgs/main-components.png)

## Výběr jazyka

Aplikace je k dispozici v angličtině, češtině a slovinštině.

Přepnutí jazyka:

1. Klikněte na ikonu **Uživatelského menu** v pravém horním rohu záhlaví
2. Najeďte myší na položku podnabídky **Jazyk**
3. Vyberte preferovaný jazyk

Rozhraní se okamžitě aktualizuje. Váš výběr jazyka je uložen napříč relacemi.

## Nastavení časového období a kontextu indikátorů

Před analýzou dat nastavte časové a indikátorové parametry v panelu **Nastavení**.

### Otevření panelu Nastavení

1. Klikněte na **Nastavení** v sbalitelném menu modulů (ve spodní části levého panelu, pod položkou **Konfigurace**)

![Panel Nastavení](../imgs/settings.png)

### Nastavení časového období

1. V panelu Nastavení najděte sekci **Časové období**
2. Nastavte **Datum začátku**: klikněte na kalendář a vyberte počáteční datum
3. Nastavte **Datum konce**: klikněte na kalendář a vyberte koncové datum
4. Volitelně nastavte **Vybrané datum**: klikněte na kalendář a vyberte datum
   - Základní vrstva mapy se aktualizuje a zobrazí snímky z daného data
   - Užitečné pro zobrazení prostorového kontextu v konkrétním časovém okamžiku

**Důležité**: Data se zobrazí pouze v případě, že ve zvoleném časovém období existují platná pozorování. Pokud se žádná data nezobrazují, zkuste upravit časové období tak, aby odpovídalo době sběru dat. Při prvním otevření rozsahu je časové období nastaveno na výchozí počáteční a koncové datum definované v konfiguraci rozsahu.

### Výběr kontextu indikátorů

1. V panelu Nastavení najděte rozbalovací nabídku **Kontext indikátorů**
2. Kliknutím na ni zobrazíte dostupné kontexty indikátorů
3. Vyberte požadovaný kontext

Kontexty indikátorů umožňují zobrazit různé sady výpočtů indikátorů, typicky představující různé výpočetní běhy nebo sezónní období.

**Poznámka**: Nastavení nyní řídí pouze **časové období** a **kontext indikátorů**. Možnosti zobrazení pozorování (sloupce, vrstva, výplň, filtr platnosti) a možnosti vizualizace signálů a indikátorů jsou přístupné přímo v jednotlivých komponentách — viz [Modul Pohled zákresu JŽ](03-foi-view-module.mdx).

## Widget mapy a navigace

**Widget mapy** zobrazuje zákresy JŽ s prostorovým kontextem a interaktivními nástroji.

### Přehled rozhraní mapy

Mapa v modulu Přehled zobrazuje prostorové rozmístění vašich zákresů JŽ.

#### Funkce mapy

- **Polygony zákresů JŽ**: Hranice polí zobrazené na mapě
- **Barevné kódování**: Zákresy JŽ mohou být obarveny jednou barvou, podle atributů zákresu JŽ nebo podle výsledků indikátorů
- **Vybraný zákres JŽ**: Ve výchozím nastavení zvýrazněn žlutým ohraničením
- **Základní vrstva**: Základní vrstvy definované v konfiguraci rozsahu
- **Překryv hexagonální mřížky H3**: Při oddálení nad práh přiblížení zákresů JŽ jsou zákresy JŽ seskupeny do hexagonálních buněk. Kliknutím na hexagonální buňku se přiblížíte a zobrazíte zákresy JŽ v ní obsažené.

#### Ovládání mapy

- **Posouvání**: Kliknutím a tažením se pohybujete po mapě
- **Přiblížení**: Pomocí tlačítek přiblížení nebo kolečka myši přibližujte nebo oddalujte
- **Přejít na vše**: Tlačítko pro přiblížení na zobrazení všech zákresů JŽ v rozsahu

### Vyhledávání na mapě

Panel **Vyhledávání na mapě** umožňuje navigaci přímo na konkrétní zákres JŽ nebo souřadnicové umístění. Podporuje automatické doplňování a při nenalezení shody zobrazuje zprávu „Žádné výsledky".

#### Navigace podle ID zákresu JŽ

1. Klikněte na vyhledávací lištu na mapě
2. Zadejte **ID zákresu JŽ** (formát: `SCOPE.FOI.123`) nebo začněte psát **referenční ID zákresu JŽ**
3. Ze seznamu vyberte odpovídající výsledek
4. Mapa se vystředí na daný zákres JŽ a ten se stane vybraným

#### Navigace podle souřadnic

1. Klikněte na vyhledávací lištu na mapě
2. Zadejte souřadnicový pár v jednom ze tří podporovaných formátů:
   - **Desetinné stupně** (EPSG:4326): `45.5, 10.2`
   - **Kardinální stupně** (EPSG:4326): `45.5°N, 10.2°E`
   - **Projikované metrické souřadnice** (CRS rozsahu): `500000, 5500000`
3. Ze seznamu vyberte souřadnicový výsledek
4. Mapa se přesune na dané místo

### Panel Náhled výběru

Levým kliknutím na zákres JŽ na mapě se otevře plovoucí panel **Náhled výběru** zobrazující souhrn atributů zákresu JŽ — včetně jeho klíčových identifikátorů. Tato funkce funguje konzistentně v Přehledu, Pohledu zákresu JŽ i Pohledu validace a je nejrychlejším způsobem, jak identifikovat parcelu a přečíst její podrobnosti bez plného otevření.

- Pokud se v místě kliknutí překrývá více zákresů JŽ, panel je všechny uvede; kliknutím na jeden rozbalíte jeho atributy
- Přesné zobrazené atributy závisí na mapování dat rozsahu (viz [Konfigurace rozsahu](05-scope-configuration.mdx)), různé rozsahy proto mohou zobrazovat různá pole
- Klikněte na
  - **Vybrat zákres JŽ** pro výběr zobrazeného zákresu JŽ
  - **Otevřít v novém záložce pohledu zákresu JŽ** pro přechod do podrobné analýzy v novém záložce prohlížeče
  - **Kopírovat** pro zkopírování ID zákresu JŽ

![Panel Náhled výběru otevřený po kliknutí na parcelu na mapě](../imgs/map-selection-preview.png)

### Nástroje

**Měřicí nástroj**:

1. Klikněte na ikonu **měřicího nástroje**
2. Zvolte režim **Změřit vzdálenost** nebo **Změřit plochu**
3. **Pro měření vzdálenosti**:
   - Klikáním na mapě vytvořte lomenou čáru
   - Vzdálenost je vypočítána a zobrazena (v metrech nebo km)
   - Dvojitým kliknutím měření ukončete
4. **Pro měření plochy**:
   - Klikáním nakreslete polygon
   - Plocha je vypočítána a zobrazena — zobrazená jednotka závisí na konfiguraci rozsahu (m², km², **ha** nebo akry)
   - Uzavřením polygonu měření ukončete

![Měřicí nástroj](../imgs/map-measure-tool.png)

**Nástroj Umístit špendlík**:

1. Klikněte na ikonu **Umístit špendlík**
2. Kliknutím kamkoli na mapě umístěte špendlík
3. Zobrazí se **přesné souřadnice** špendlíku
4. Slouží ke kopírování souřadnic
5. Kliknutím na `X` špendlíku ho odstraňte

![Nástroj Umístit špendlík](../imgs/map-drop-pin.png)

**Vytvořit foto úkol**:

Pokud má váš účet oprávnění k vytváření foto úkolů, je na mapě k dispozici tlačítko **Vytvořit foto úkol**. Nejprve vyberte zákres JŽ, poté klikněte na tlačítko a otevřete správce foto úkolů pro dané pole. Toto tlačítko je skryto, pokud přístup k fotografiím není pro daný rozsah povolen.

**Posouvání a přiblížení**:

- **Posouvání**: Kliknutím a tažením pohybujte mapou
- **Přiblížení**: Použijte tlačítka přiblížení nebo kolečko myši
- **Přejít na zákres JŽ**: Tlačítko pro vystředění a přiblížení na vybraný zákres JŽ

### Správa vrstev

#### Základní vrstva

**Změna základní vrstvy pozadí**:

1. Klikněte na tlačítko nebo ikonu **Vrstvy**
2. Otevře se panel správy vrstev
3. Vyberte z dostupných základních vrstev:
   - **Letecká fotografie**: Letecké snímky s vysokým rozlišením
   - **Snímky Sentinel nebo Planet**: Vrstvy definované v propojené konfiguraci Planet Insight Platform
   - Jiné vrstvy specifické pro rozsah definované v jeho konfiguraci
4. Pozadí mapy se okamžitě aktualizuje

![Správa základní vrstvy](../imgs/map-base-layers.png)

**Poznámka — názvy vrstev a výchozí nastavení pocházejí z konfigurace rozsahu**: Seznam dostupných základních vrstev, jejich názvy a výchozí vrstva jsou definovány správcem rozsahu. Pokud název vrstvy vypadá nesprávně nebo zastarale, nebo se vrstva vůbec nenačítá, jedná se o záležitost konfigurace rozsahu, nikoli o něco, co lze opravit v aplikaci. Obraťte se na svého správce (viz [Konfigurace rozsahu](05-scope-configuration.mdx)) nebo kontaktujte podporu Planet (viz [Podpora a řešení problémů](07-support-troubleshooting.mdx)).

#### Možnosti vizualizace zákresů JŽ

**Přepínání popisků a ohraničení zákresů JŽ**:

1. Otevřete panel **Nastavení vrstev**
2. Najděte možnosti **Vizualizace zákresů JŽ**:
   - **Zobrazit popisky**: Zobrazení atributových popisků zákresu JŽ (např. ID zákresu JŽ, typ plodiny). Popisky se zobrazí po přiblížení nad práh přiblížení nakonfigurovaný pro daný typ zákresu JŽ.
   - **Zobrazit ohraničení**: Zobrazení obrysů polygonů zákresů JŽ
   - **Zobrazit výplň**: Vyplnění polygonů zákresů JŽ barvou
   - **Průhlednost**: Nastavení průhlednosti (0–100 %)
3. Zapínáním a vypínáním možností si přizpůsobte zobrazení

**Obarvit zákresy JŽ podle**:
Zákresy JŽ lze obarvit různými způsoby:

1. **Jednotná barva**:
   - Ze seznamu vyberte jednu z dostupných barev
   - Všechny zákresy JŽ jsou vizualizovány vybranou barvou

2. **Atributy**:
   - Ze seznamu vyberte jeden z dostupných atributů
   - Barvy reprezentují různé hodnoty vybraného atributu (např. typ plodiny)

3. **Výsledky indikátorů**:
   - Zákresy JŽ jsou obarveny na základě předpovědí indikátorů
   - Ze seznamu vyberte jeden z dostupných indikátorů
   - Příklady: zelená pro „schváleno", červená pro „označeno"
   - Barvy definované konfigurací rozsahu

![Možnosti vizualizace zákresů JŽ](../imgs/map-foi-visualization-options.png)

**Poznámka — výchozí nastavení popisků pochází z konfigurace rozsahu**: Které popisky jsou zobrazeny a zda jsou ve výchozím stavu zapnuty, je nastaveno pro každý typ zákresu JŽ v konfiguraci rozsahu. Pokud jsou popisky při otevření aplikace již viditelné, jde o očekávané výchozí nastavení pro váš rozsah — váš správce je takto nakonfiguroval. Uživatel může tato výchozí nastavení zobrazení kdykoli přizpůsobit svým potřebám.

**Zvýraznění vybraného zákresu JŽ**:

- Aktuálně vybraný zákres JŽ je ve výchozím nastavení zvýrazněn **žlutým ohraničením a výplní**
- Usnadňuje identifikaci zákresu JŽ, který analyzujete
- Barvu ohraničení lze změnit v možnostech vrstvy

## Widget seznamu/tabulky zákresů JŽ

### Funkce

**Filtrovatelná tabulka**:

- Všechny zákresy JŽ v aktuálním rozsahu zobrazené v tabulce
- Pomocí **filtru** v horní části filtrujte zákresy JŽ
- Vyberte libovolný atribut a zadejte hodnotu pro rychlé vyhledání zákresů JŽ

**Správa sloupců**:

- Kliknutím na **Spravovat sloupce** zobrazíte nebo skryjete sloupce
- Přizpůsobte, které atributy zákresu JŽ se zobrazí
- Sloupce mohou zahrnovat: ID zákresu JŽ, Typ, Plodina, Využití půdy, Plocha atd.

**Stránkování**:

- Při velkém počtu zákresů JŽ procházejte mezi stránkami
- Čísla stránek jsou zobrazena ve spodní části tabulky

**Rychlé přepínání zákresu JŽ**:

- Kliknutím na libovolný řádek přepnete na daný zákres JŽ
- Aktuálně vybraný zákres JŽ je v tabulce zvýrazněn

![Filtr zákresů JŽ](../imgs/foi-filter-columns.png)

## Vyhledávání a výběr zákresů JŽ

Aplikace AM poskytuje více způsobů výběru zákresu JŽ. Zvolte metodu, která nejlépe odpovídá vašemu pracovnímu postupu:

### Metoda 1: Výběr v seznamu zákresů JŽ

1. Podívejte se do panelu **Seznam zákresů JŽ** (na levé straně)
2. Klikněte na zákres JŽ v seznamu
3. Mapa se vystředí na daný zákres JŽ a načtou se jeho data
4. Kliknutím na akční tlačítko vstoupíte do podrobného Pohledu zákresu JŽ

### Metoda 2: Filtrování

Lišta filtru umožňuje zúžit seznam zákresů JŽ podle jedné nebo více hodnot atributů.

Pokud například znáte typ zákresu JŽ a referenční ID:

1. Podívejte se do panelu **Seznam zákresů JŽ** (na levé straně)
2. **Použijte lištu filtru** v horní části seznamu
3. Pomocí rozbalovací nabídky výběru atributu zvolte atribut pro filtrování
4. Pomocí **Typ zákresu JŽ** vyberte typ (např. „Pole", „Parcela")
5. Pomocí **Referenční ID zákresu JŽ** zadejte jedno nebo více referenčních ID do pole filtru
6. Seznam se aktualizuje a zobrazí pouze odpovídající zákresy JŽ
7. Kliknutím na akční tlačítko vstoupíte do podrobného Pohledu zákresu JŽ

![Filtr zákresů JŽ](../imgs/foi-filter-foi-type-ref-id.png)

### Metoda 3: Vyhledávání na mapě

Pomocí panelu **Vyhledávání na mapě** na mapě přejdete přímo na zákres JŽ podle jeho ID nebo referenčního ID, nebo navigujete na konkrétní souřadnice. Podrobnosti viz [Widget mapy a navigace](#widget-mapy-a-navigace).

### Metoda 4: Interakce s mapou

Vyberte zákresy JŽ přímo z mapy:

1. Najděte svůj zákres JŽ na mapě
2. **Levým tlačítkem** klikněte na polygon zákresu JŽ a otevřete panel **Náhled výběru** s jeho atributy
3. **Pravým tlačítkem** klikněte na polygon zákresu JŽ a přejděte přímo do podrobného Pohledu zákresu JŽ

**Tip**: Posouvejte a přibližujte mapu pro průzkum různých oblastí. Při oddálení klikněte na hexagonální buňku a přibližte se na danou skupinu zákresů JŽ.

Pokročilé funkce analýzy zákresu JŽ jsou popsány v [Modul Pohled zákresu JŽ](03-foi-view-module.mdx).

---
